import { useMemo, useState } from "react"
import { FieldType, UIPermission, Column, FieldValue, SpinnerDiv, useUIActions, ModalId, Action, Data, Badge, ButtonKind, O, TotalAmountSection } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { useFind } from "../../../../../state/reducers/hooks"
import { define, toNumber } from "../../../../../utils/typeUtils"
import { DataTypeList, Labels } from "../../../../../features/data-types"
import { NO_SUPPLIER_LABEL, Supplier } from "../../../../purchases"
import { Expense, ExpenseType, Ticket } from "../../../state/types"
import { ExpenseFilters } from "./expenseFilters"
import { useLocation } from "react-router-dom"
import { PaymentStatus } from "../../../../operations"
import { DownloadExpenseFilesModal } from "../../download-expense-files-modal/downloadExpenseFilesModal"

type Props = {
    expenses: Expense[]
    tickets: Ticket[]
}

export const ExpenseList = (props: Props) => {
    const { expenses, tickets } = props

    const [rows, setRows] = useState<Data[]>([])
    const [currentExpense, setCurrentExpense] = useState<Expense>()
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_EXPENSES)
    const { pathname } = useLocation()
    const find = useFind()

    const { openModal } = useUIActions()

    const totalAmount = useMemo(() => rows.reduce((acc, row) => acc + (row as Expense).amount, 0), [rows])

    const columns: Column[] = [
        { name: 'code', type: FieldType.NUMBER, label: '#', render: (value?: FieldValue) => `#${value}` },
        { name: 'name', type: FieldType.TEXT, label: 'Nombre' },
        {
            name: 'expenseTypeId',
            type: FieldType.TABLE,
            table: TableName.EXPENSE_TYPES,
            label: 'Tipo',
            render: (value?: FieldValue) => {
                const expenseType = define(find(TableName.EXPENSE_TYPES, value as string)) as ExpenseType
                return <Badge label={expenseType.name} />
            },
            sortValue: (value?: FieldValue) => (find(TableName.EXPENSE_TYPES, value as string) as O<ExpenseType>)?.name ?? ''
        },
        { name: 'date', type: FieldType.DATE, label: 'Fecha' },
        {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            label: 'Proveedor',
            render: (value?: FieldValue) => {
                const supplier = find(TableName.SUPPLIERS, value as O<string>) as O<Supplier>
                return <Badge label={supplier?.name || NO_SUPPLIER_LABEL} />
            },
            sortValue: (value?: FieldValue) => (find(TableName.SUPPLIERS, value as O<string>) as O<Supplier>)?.name ?? NO_SUPPLIER_LABEL
        },
        {
            name: 'amount',
            type: FieldType.PRICE,
            label: 'Monto'
        },
        {
            name: 'paymentStatus',
            type: FieldType.TEXT,
            label: 'Estado de Pago',
            sortValue: (value?: FieldValue) => Labels.paymentStatusLabel(value as PaymentStatus),
            render: (value?: FieldValue) => Labels.paymentStatusLabel(define(value) as PaymentStatus)
        }
    ]

    const findExpense = (code?: string) => expenses.find(expense => expense.code === toNumber(code))
    const findTicket = (parentId?: string) => tickets.find(ticket => ticket.parentId === parentId)
    
    const onDownloadFiles = (code?: string) => {
        setCurrentExpense(findExpense(code))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onRemove = (code?: string) => {
        setCurrentExpense(findExpense(code))
        openModal(ModalId.REMOVE)
    }

    const actions = (code: string): Action[] => {
        const expense = findExpense(code)
        const expenseTicket = findTicket(expense?.id)
        const isPaymentPending = expense?.paymentStatus === PaymentStatus.PENDING
        const payedAmount = expenseTicket?.payedAmount || 0
        const isEditable = isPaymentPending && payedAmount === 0

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary' as ButtonKind, tooltip: 'Ver Detalle', href: `${pathname}/${expense?.id}` }
        ]
        if (expenseTicket?.ticketUrl) {
            actionList.push({ icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles })
        }
        if (!readOnly && isEditable) {
            actionList.push({ icon: 'trash-fill', kind: 'secondary', tooltip: 'Eliminar', onClick: onRemove })
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        setRows(dataTypes as Expense[])
        setAsyncDataLoaded(true)
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <ExpenseFilters
                expenses={expenses}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            <TotalAmountSection
                totalAmount={totalAmount}
                loaded={asyncDataLoaded}
                label="Total Gastos"
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    rowId="code"
                    currentDataTypeParam={currentExpense}
                    actions={actions}
                    readOnly={readOnly}
                />
            )}
            <DownloadExpenseFilesModal expense={currentExpense} />
        </>
    )
}
